'use client';
// Inspired by react-hot-toast library
import * as React from 'react';

const TOAST_LIMIT = 1;
const TOAST_REMOVE_DELAY = 1000000;

const variantStyles = {
  success: 'bg-toast-success-bg border-toast-success-border text-toast-success-text border hover:bg-toast-success-bg/90',
  danger: 'bg-toast-danger-bg text-toast-danger-text border-toast-danger-border border hover:bg-toast-danger-bg/90',
  warning: 'bg-toast-warning-bg text-toast-warning-text border-toast-warning-border border hover:bg-toast-warning-bg/90',
  info: 'bg-toast-info-bg text-toast-info-text border-toast-info-border border hover:bg-toast-info-bg/90',
  neutral: 'bg-toast-neutral-bg text-toast-neutral-text border-toast-neutral-border border hover:bg-toast-neutral-bg/90'
};

let count = 0;

/**
 * Generate a unique id for the toast
 * @returns {string}
 */
function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

const toastTimeouts = new Map();

/**
 * Add a toast to the remove queue
 * @param toastId
 */
const addToRemoveQueue = (toastId) => {
  if (toastTimeouts.has(toastId)) {
    return;
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({
      type: 'REMOVE_TOAST',
      toastId: toastId
    });
  }, TOAST_REMOVE_DELAY);

  toastTimeouts.set(toastId, timeout);
};

export const reducer = (state, action) => {
  switch (action.type) {
    case 'ADD_TOAST':
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT)
      };

    case 'UPDATE_TOAST':
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t)
      };

    case 'DISMISS_TOAST': {
      const { toastId } = action;

      // ! Side effects ! - This could be extracted into a dismissToast() action,
      // but I'll keep it here for simplicity
      if (toastId) {
        addToRemoveQueue(toastId);
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id);
        });
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
              ...t,
              open: false
            }
            : t)
      };
    }
    case 'REMOVE_TOAST':
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: []
        };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId)
      };
  }
};

const listeners = [];

let memoryState = { toasts: [] };

/**
 *
 * @param action
 */
function dispatch(action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}

/**
 *
 * @param root0
 * @param props
 * @returns {*|{id: *, dismiss: *, update: *}}
 */
function toast({
  ...props
}) {
  const id = genId();

  const update = (props) =>
    dispatch({
      type: 'UPDATE_TOAST',
      toast: { ...props, id }
    });
  const dismiss = () => dispatch({ type: 'DISMISS_TOAST', toastId: id });

  dispatch({
    type: 'ADD_TOAST',
    toast: {
      ...props,
      className: variantStyles[props.variant] || variantStyles.default,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) { dismiss(); }
      }
    }
  });

  return {
    id: id,
    dismiss,
    update
  };
}

/**
 * Use toast hook
 * @returns {*|{toast: *, dismiss: *}}
 */
function useToast() {
  const [state, setState] = React.useState(memoryState);

  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, [state]);

  return {
    ...state,
    toast,
    dismiss: (toastId) => dispatch({ type: 'DISMISS_TOAST', toastId })
  };
}

export { useToast, toast };
